import { useCallback, useEffect, useState } from 'react';
import { BookOpen, User } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
  toast,
} from '@ludolio/shared-ui';
import { useLabsReview } from './context';
import { formatDate } from './format';
import { LabsReviewDetail } from './LabsReviewDetail';
import {
  ListCell,
  MetaItem,
  PlatformChips,
  ProjectHeading,
  ProjectList,
  ProjectRow,
  PUBLISHED_LIST_COLUMNS,
  RowActions,
  VisibilityCell,
} from './ProjectRow';
import type { LabPublishedItem, LabReviewScope } from './types';

interface LabsPublishedListProps {
  scope: LabReviewScope;
  unitId?: string;
  emptyMessage: string;
  onChanged?: () => void;
}

export function LabsPublishedList({ scope, unitId, emptyMessage, onChanged }: LabsPublishedListProps) {
  const { api, getErrorMessage } = useLabsReview();
  const canReadResponses = scope === 'organization';
  const [items, setItems] = useState<LabPublishedItem[] | null>(null);
  const [error, setError] = useState('');
  const [target, setTarget] = useState<LabPublishedItem | null>(null);
  const [viewing, setViewing] = useState<LabPublishedItem | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .listPublished(scope, unitId)
      .then((result) => {
        setItems(result ?? []);
        setError('');
      })
      .catch((err) => setError(getErrorMessage(err)));
  }, [api, getErrorMessage, scope, unitId]);

  useEffect(() => {
    load();
  }, [load]);

  const takedown = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await api.takedown(target.projectId, note.trim());
      toast.success('Project taken down');
      setTarget(null);
      setNote('');
      load();
      onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {!items && !error && <p className="text-sm text-muted-foreground">Loading…</p>}
      {items?.length === 0 && (
        <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">{emptyMessage}</div>
      )}
      {items && items.length > 0 && (
        <ProjectList columns={PUBLISHED_LIST_COLUMNS} headings={['Project', 'Who can play', 'Published']}>
          {items.map((item) => (
            <ProjectRow key={item.releaseId} columns={PUBLISHED_LIST_COLUMNS}>
              <ProjectHeading title={item.title} version={item.version} iconUrl={item.iconUrl}>
                {!unitId && (
                  <MetaItem icon={BookOpen} label="Unit">
                    {item.unit ? <span className="font-medium">{item.unit.code}</span> : 'No unit'}
                  </MetaItem>
                )}
                {item.submittedBy && <MetaItem icon={User} label="Submitted by">{item.submittedBy}</MetaItem>}
                <PlatformChips platforms={item.platforms} />
              </ProjectHeading>
              <VisibilityCell visibility={item.visibility} />
              <ListCell label="Published">{formatDate(item.publishedAt)}</ListCell>
              <RowActions>
                <Button size="sm" variant="outline" onClick={() => setViewing(item)}>View</Button>
                <Button size="sm" variant="outline" onClick={() => { setNote(''); setTarget(item); }}>Take down</Button>
              </RowActions>
            </ProjectRow>
          ))}
        </ProjectList>
      )}

      <LabsReviewDetail releaseId={viewing?.releaseId ?? null} onClose={() => setViewing(null)} canReadResponses={canReadResponses} />

      <Dialog open={Boolean(target)} onOpenChange={(open) => !open && setTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Take down {target?.title}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="takedown-note">Reason</Label>
            <Textarea id="takedown-note" rows={3} maxLength={2000} value={note} onChange={(event) => setNote(event.target.value)} disabled={busy} />
            <p className="text-xs text-muted-foreground">The project team sees this reason. Players can no longer download or launch the game.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setTarget(null)}>Cancel</Button>
            <Button variant="destructive" disabled={busy || !note.trim()} onClick={() => void takedown()}>Take down</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
